// Session 49 (Wave 14): "Newsletters ready", the newsletter's own page.
//
// Leena, 2026-10-12: "The next big one should be Newsletters ... when you
// click down it should say Newsletters Ready. When you click on them it
// should show which blogs have newsletters. For every blog posted there
// should be a newsletter there immediately and it should show how it works."
//
// One row per published post. The newest two without a newsletter yet are
// "Making it" (the reactor builds one per tick for a client whose newsletter
// just came on, pack 3); anything older than that never had one, and says so
// rather than pretending to be on its way. "What to do with it" sits at the
// top, the Wave 11 rule: beside the download, not inside the file.
//
// A client without the newsletter never reaches this page from the rail (the
// entry is locked and opens the Learn more page); typing the address lands on
// the same Learn more page. The agency sees it for the selected client.
import { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { supabase } from '../supabase.js';
import { useAuth } from '../AuthContext.jsx';
import { clientPath } from '../lib/clientLink.js';
import NewsletterInstructions from '../components/NewsletterInstructions.jsx';
import NewsletterCode from '../components/NewsletterCode.jsx';
import NewsletterEmailLines from '../components/NewsletterEmailLines.jsx';

const BACKFILL = 2;   // the reactor's NEWSLETTER_BACKFILL_POSTS, Leena's "last two"

function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(String(iso).slice(0, 10) + 'T12:00:00');
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

async function downloadNewsletter(row) {
  const { data, error } = await supabase.storage.from('post-packages').createSignedUrl(row.newsletter_path, 300);
  if (error) throw error;
  window.location.href = data.signedUrl;
}

export default function Newsletters() {
  const { activeId, activeClient, isAgency, entitlements } = useAuth();
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);
  const [setup, setSetup] = useState(null);    // the client's own look is saved (null = unknown / agency)
  const on = (entitlements || []).includes('newsletter');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setRows(null); setError(null);
      if (!activeId || !on) return;
      const { data, error: e } = await supabase
        .from('v_portal_posts').select('*').eq('client_id', activeId)
        .order('publish_date', { ascending: false, nullsFirst: false });
      if (cancelled) return;
      if (e) setError(e.message); else setRows(data || []);
      // Review 9: "Making it" is only true once a look is saved; the view is
      // the client's own (the agency reads nothing from it, and sees neutral text).
      if (!isAgency) {
        const { data: me } = await supabase.from('v_portal_onboarding_me').select('newsletter').maybeSingle();
        if (!cancelled) setSetup(!!me?.newsletter?.template);
      }
    })();
    return () => { cancelled = true; };
  }, [activeId, on, isAgency]);

  if (!on && !isAgency) return <Navigate to={clientPath('/learn/email', activeId)} replace />;

  if (!on && isAgency) {
    return (
      <>
        <h1>Newsletters ready</h1>
        <p className="lede">
          {activeClient?.name || 'This client'} does not have the newsletter yet. Switch it on in{' '}
          <Link to={`/admin/clients/${activeId}/settings?client=${activeId}`}>their settings</Link>, or let them add it
          themselves from <Link to={clientPath('/learn/email', activeId)}>the Learn more page</Link> they see.
        </p>
      </>
    );
  }

  if (error) return <div className="empty"><h2>Couldn't load your newsletters</h2><p>{error}</p></div>;
  if (rows === null) return <p className="lede">Loading your newsletters…</p>;

  const published = rows.filter((r) => r.status === 'published' || r.status === 'delivered');
  const ready = published.filter((r) => r.newsletter_ready);

  return (
    <>
      <div className="section-heading">
        <h1>Newsletters ready</h1>
        <NewsletterInstructions />
      </div>
      <p className="lede">
        One for every post that goes out, in your brand: a PDF to read, and the same email as code
        to paste into Mailchimp or any email tool. Under each one, a subject line, a second option and
        the preview text, ready to copy.
        {ready.length > 0 ? ` ${ready.length} ready.` : ''}
      </p>

      {setup === false && (
        <div className="banner banner-info">
          Pick how your newsletter should look first, and your last two posts get one straight away.{' '}
          <Link to={clientPath('/welcome?added=email', activeId)}>Set up my newsletter</Link>
        </div>
      )}

      {published.length === 0 && (
        <div className="empty">
          <h2>Nothing here yet</h2>
          <p>Your first newsletter arrives with your first published post.</p>
        </div>
      )}

      {published.length > 0 && (
        <div className="card">
          {published.map((r, i) => (
            <div className="blog-row" key={r.slug}>
              <div className="title">
                <Link to={clientPath(`/blogs/${r.slug}`, r.client_id)}>{r.title || r.slug}</Link>
                <span className="sub">
                  {r.newsletter_ready ? 'Newsletter ready'
                    : (i < BACKFILL ? (setup === false ? 'Waiting for your look' : 'Making it, back in a few minutes')
                                    : 'From before your newsletter started')}
                </span>
              </div>
              <div className="date">{formatDate(r.publish_date)}</div>
              <div className="actions">
                {r.newsletter_ready ? (
                  <>
                    <button className="btn btn-primary" onClick={() => downloadNewsletter(r).catch((e) => alert(e.message))}>
                      Download newsletter
                    </button>
                    <NewsletterCode row={r} />
                  </>
                ) : (
                  i < BACKFILL && setup !== false && <span className="chip chip-run-wait">Making it…</span>
                )}
              </div>
              {r.newsletter_ready && <NewsletterEmailLines row={r} />}
            </div>
          ))}
        </div>
      )}
    </>
  );
}
